import { ModelBase } from 'store/model-base';
import { DataPlanType, DeviceType, LineStatus, LineStatusDisplay, UnlPlanSubgroup } from 'my-account/constants';

export class LineSummary extends ModelBase {
    public static storeName: string = 'lineSummary';

    public id: Id;
    public displayName: string;
    public lineNumber: string;
    public dataUsage: number;
    public status: LineStatus;
    public planCode: string;
    public planId: string;
    public pairedLines: PairedLine[];
    public device: AccountLineDevice;
    public postSwitch: PostSwitchValues;
    public planPrice: number;
    public numberOfLinesInThePlan: number;
    public planSubgroup: UnlPlanSubgroup | DataPlanType;
    public scheduledSwitch?: ScheduledSwitch;

    public makeAndModel: string;
    public iconLetter: string;
    public isActive: boolean;
    public isPending: boolean;
    public isSuspended: boolean;
    public isDeactivated: boolean;
    public isActiveOrSuspended: boolean;
    public isPendingOrDeactivated: boolean;
    public isLegacy: boolean;
    public isUnlimited: boolean;
    public isTablet: boolean;
    public isWatch: boolean;
    public isDevice: boolean;
    public hideDataOptions: boolean;
    public isScheduledSwitch: boolean;
    public isScheduledSwitchSubGroup: boolean;

    public displayStatus: string;
    public color: string;
    public planName: string;
    public pairedLineNumber: string;
    public lineDataUsage: number;
    public promotionContent: string;
    public dataOptions: {
        options: DropdownItem[];
        isEditable: boolean;
        defaultOption?: DropdownItem[];
    };
    
    public static create<T extends ModelBase>(data: ApiResponse = {}): T {
        const toReturn: LineSummary = super.create<LineSummary>(data);
        toReturn.id = toReturn.id || { key: '', value: '' };
        toReturn.displayName = toReturn.displayName || '';
        toReturn.makeAndModel = data.device ? `${data.device.make} ${data.device.model}` : '';
        toReturn.lineNumber = toReturn.lineNumber || '';
        toReturn.iconLetter = toReturn.displayName[0];
        toReturn.isActive = toReturn.status === LineStatus.ACTIVE;
        toReturn.isPending = toReturn.status === LineStatus.PENDING;
        toReturn.isSuspended = toReturn.status === LineStatus.SUSPENDED;
        toReturn.isDeactivated = toReturn.status === LineStatus.DEACTIVATED;
        toReturn.displayStatus = LineSummary.getDisplayStatus(toReturn);
        toReturn.isActiveOrSuspended = toReturn.isActive || toReturn.isSuspended;
        toReturn.isPendingOrDeactivated = toReturn.isPending || toReturn.isDeactivated;
        toReturn.isDevice = Boolean(toReturn.device && toReturn.device.deviceType &&
            !(toReturn.device.deviceType === DeviceType.SMART_WATCH || toReturn.device.deviceType === DeviceType.TABLET));
        toReturn.hideDataOptions = Boolean(toReturn.postSwitch);
        toReturn.isTablet = Boolean(toReturn.device && toReturn.device.deviceType && toReturn.device.deviceType === DeviceType.TABLET);
        toReturn.isWatch = Boolean(toReturn.device && toReturn.device.deviceType && toReturn.device.deviceType === DeviceType.SMART_WATCH);
        toReturn.isScheduledSwitch = Boolean(toReturn.scheduledSwitch);
        toReturn.isScheduledSwitchSubGroup = toReturn.isScheduledSwitch && Boolean(toReturn.scheduledSwitch.planSubgroup);

        if (toReturn.pairedLines) {
            toReturn.pairedLines = toReturn.pairedLines.map((pairedLine: PairedLine) => {
                pairedLine.lineId = pairedLine.lineId || pairedLine.id;

                return pairedLine;
            });
        } else {
            toReturn.pairedLines = [];
        }

        /* eslint-disable @typescript-eslint/no-explicit-any */
        return <T> <any> toReturn;
        /* eslint-enable @typescript-eslint/no-explicit-any */
    }

    public get isVideoResolution(): boolean {
        return this.isActive && (this.isTablet || (this.isDevice && (this.planSubgroup === UnlPlanSubgroup.PREMIUM || this.planSubgroup === UnlPlanSubgroup.PLUS || this.planSubgroup === UnlPlanSubgroup.SELECT)));
    }

    public get isBtgLine(): boolean {
        return this.isActive && (this.planSubgroup === DataPlanType.BY_THE_GIG || this.planSubgroup === DataPlanType.BY_THE_GIG_SHARED);
    }

    public get isUnlBasic(): boolean {
        return this.isActive && this.planSubgroup === UnlPlanSubgroup.BASIC;
    }

    public get isUnlPlus(): boolean {
        return this.isActive && this.planSubgroup === UnlPlanSubgroup.PLUS;
    }

    public get isUnlPremium(): boolean {
        return this.isActive && this.planSubgroup === UnlPlanSubgroup.PREMIUM;
    }

    public get isStandaloneWatch(): boolean {
        return this.isActive && this.planSubgroup === UnlPlanSubgroup.STANDALONE && this.device.deviceType === DeviceType.SMART_WATCH;
    }

    private static getDisplayStatus(data: ApiResponse): string {
        if (data.isPending) {
            return LineStatusDisplay.PENDING;
        } else if (data.isSuspended) {
            return LineStatusDisplay.SUSPENDED;
        } else if (data.isDeactivated) {
            return LineStatusDisplay.DEACTIVATED;
        } else {
            return '';
        }
    }
}
